import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Autocomplete,
    TextField,
    Button,
    Stack,
    Chip,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import { listLabTests, listRadiologyTests } from '../../../../api/emrMaster';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';

function Picker({ label, icon, options, items, disabled, onAdd, onRemove, category }) {
    const [pick, setPick] = useState(null);
    const [input, setInput] = useState('');

    // Add a coded option when one is selected, otherwise the typed free text
    // (the "Other" / free-format case) with no code.
    const handleAdd = () => {
        if (pick && typeof pick === 'object') {
            onAdd(category, { code: pick.code, name: pick.name });
        } else if (input.trim()) {
            onAdd(category, { code: '', name: input.trim() });
        } else {
            return;
        }
        setPick(null);
        setInput('');
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'rgba(5,47,93,0.10)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                {icon}
                <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 14.5 }}>{label}</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', ml: 'auto' }}>
                    {items.length} ordered
                </Typography>
            </Box>
            {!disabled && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
                    <Autocomplete
                        freeSolo
                        sx={{ flex: 1 }}
                        size="small"
                        options={options}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name || ''))}
                        groupBy={(o) => (typeof o === 'string' ? '' : (o.category || 'Other'))}
                        value={pick}
                        inputValue={input}
                        onInputChange={(_, v) => setInput(v)}
                        onChange={(_, v) => setPick(v)}
                        isOptionEqualToValue={(a, b) => a?.code === b?.code}
                        renderInput={(p) => <TextField {...p} label={`Add ${label.toLowerCase()} (or type your own)`} />}
                    />
                    <Button
                        onClick={handleAdd}
                        disabled={!pick && !input.trim()}
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        sx={{
                            bgcolor: NAVY,
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                        }}
                    >
                        Add
                    </Button>
                </Stack>
            )}
            {items.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    No {label.toLowerCase()} ordered.
                </Typography>
            ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {items.map((it, idx) => (
                        <Chip
                            key={`${it.code}-${idx}`}
                            label={it.name}
                            onDelete={disabled ? undefined : () => onRemove(it)}
                            sx={{
                                bgcolor: 'rgba(5,47,93,0.06)',
                                color: NAVY,
                                fontWeight: 500,
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Paper>
    );
}

export default function InvestigationTab({ encounter, onPatch, disabled }) {
    const [labOptions, setLabOptions] = useState([]);
    const [radOptions, setRadOptions] = useState([]);

    useEffect(() => {
        listLabTests().then((r) => setLabOptions(r.data || [])).catch(() => {});
        listRadiologyTests().then((r) => setRadOptions(r.data || [])).catch(() => {});
    }, []);

    const items = encounter?.investigations || [];
    const labs = items.filter((i) => i.category === 'lab');
    const rads = items.filter((i) => i.category === 'radiology');

    const addItem = (category, opt) => onPatch({
        investigations: [...items, { category, code: opt.code, name: opt.name }],
    });
    const removeItem = (target) => onPatch({
        investigations: items.filter((i) => !(i.category === target.category && i.code === target.code && i.name === target.name)),
    });

    return (
        <Box>
            <SectionHeading
                title="Investigations to order"
                hint="Pick the labs and imaging studies the patient needs."
            />
            <Stack spacing={2}>
                <Picker
                    label="Laboratory"
                    icon={<ScienceOutlinedIcon sx={{ color: NAVY }} />}
                    options={labOptions}
                    items={labs}
                    disabled={disabled}
                    category="lab"
                    onAdd={addItem}
                    onRemove={removeItem}
                />
                <Picker
                    label="Radiology"
                    icon={<MonitorHeartOutlinedIcon sx={{ color: NAVY }} />}
                    options={radOptions}
                    items={rads}
                    disabled={disabled}
                    category="radiology"
                    onAdd={addItem}
                    onRemove={removeItem}
                />
            </Stack>
        </Box>
    );
}
